import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { FunnelChart } from '../components/analytics/FunnelChart';
import { EventSpecTable } from '../components/analytics/EventSpecTable';
import { DateRangePicker } from '../components/analytics/DateRangePicker';
import { DEFAULT_RANGE, RangeOption, headlinesFor, flowFor, cohortsFor, fmtNum, money } from '../data/onboardingFunnel';

/**
 * Onboarding conversion — the business numbers first, then the flow from the
 * Sign in click to a paid subscription and where people drop off. Every number
 * is scoped to the selected reporting period.
 */
export function OnboardingFunnel() {
  const [range, setRange] = useState<RangeOption>(DEFAULT_RANGE);
  const headlines = useMemo(() => headlinesFor(range), [range]);
  const flow = useMemo(() => flowFor(range), [range]);
  const cohorts = useMemo(() => cohortsFor(range), [range]);
  return <div className="min-h-screen w-full bg-[#FAF8F4] font-sans text-[#2B211A]" data-id="element-10321" data-mp="mlk0kbtpr5gs">
      <div className="mx-auto max-w-5xl px-4 py-8 md:px-8 md:py-10" data-id="element-10322" data-mp="28cxc9mocj3jy">
        <header className="flex flex-wrap items-end justify-between gap-4" data-id="element-10323" data-mp="23engpaypm0xt">
          <div data-id="element-10324" data-mp="2ibe7wwy8c6cc">
            <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-neutral-400" data-id="element-10325" data-mp="2ibwascfedit8">
              Internal · Growth
            </p>
            <h1 className="mt-1.5 font-title text-[28px] font-bold leading-tight md:text-[34px]" data-id="element-10326" data-mp="2dduuu0d9pzlz">
              Onboarding conversion
            </h1>
          </div>
          <DateRangePicker value={range} onChange={setRange} data-id="element-10327" data-mp="2mbopl6vdwl71" />
        </header>

        {/* Business headline metrics */}
        <section className="mt-7 grid grid-cols-2 gap-3 lg:grid-cols-3" aria-label="Business metrics" data-id="element-10328" data-mp="310krem6ul1z7">
          {headlines.map(m => {
          // A rise in refunds is bad news, so the arrow colour follows intent.
          const positive = m.tone === 'bad' ? m.delta <= 0 : m.delta >= 0;
          return <div key={m.label} className="rounded-xl border border-neutral-200 bg-white p-4" data-id="element-10329" data-mp="362a5ybta3xs1">
                <p className="text-[12px] font-semibold text-neutral-500" data-id="element-10330" data-mp="3d5gnyzgj0bzv">
                  {m.label}
                </p>
                <p className="mt-1.5 flex items-baseline gap-2" data-id="element-10331" data-mp="3i0dyybxd8vfe">
                  <motion.span key={m.value} initial={{
                opacity: 0,
                y: 4
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.2
              }} className="text-[26px] font-bold leading-none" data-id="element-10332" data-mp="3n08ooc5eneyv">
                    {m.value}
                  </motion.span>
                  <span className={`inline-flex items-center gap-0.5 text-[12px] font-bold ${positive ? 'text-emerald-600' : 'text-red-600'}`} data-id="element-10333" data-mp="9mnfv26iofd3">
                    {m.delta >= 0 ? <ArrowUpRight size={12} data-id="element-10334" data-mp="ejnif345qzzg" /> : <ArrowDownRight size={12} data-id="element-10335" data-mp="2betenrmchi9v" />}
                    {Math.abs(m.delta)}
                  </span>
                </p>
                <p className="mt-1 text-[12px] text-neutral-400" data-id="element-10336" data-mp="2gdwn7m1ja6ml">
                  {m.sub} · {range.comparison}
                </p>
              </div>;
        })}
        </section>

        {/* The flow */}
        <section className="mt-9" data-id="element-10337" data-mp="spz1x7n1j6k7">
          <h2 className="text-[17px] font-bold" data-id="element-10338" data-mp="opyb0nir92w9">The flow</h2>
          <p className="mt-0.5 text-[13px] text-neutral-500" data-id="element-10339" data-mp="oqgdw2zxafd5">
            Every point between the Sign in click and a subscription. Drop-off is
            measured against the point above it.
          </p>
          <div className="mt-3" data-id="element-10340" data-mp="1ggbg308vpzj8">
            <FunnelChart steps={flow} data-id="element-10341" data-mp="1cfj4w1gg5kkb" />
          </div>
        </section>

        {/* Cohorts */}
        <section className="mt-9" data-id="element-10342" data-mp="17gfyacktj6a8">
          <h2 className="text-[17px] font-bold" data-id="element-10343" data-mp="17fdbafqm9nzl">By signup week</h2>
          <p className="mt-0.5 text-[13px] text-neutral-500" data-id="element-10344" data-mp="12hljrmtvw3vx">
            The starter credit takes a median of 17 days to spend, so weeks marked
            "too early" cannot have reached the paywall yet.
          </p>

          <div className="mt-3 overflow-hidden rounded-xl border border-neutral-200 bg-white" data-id="element-10345" data-mp="3n7awnub11flz">
            <table className="w-full text-left" data-id="element-10346" data-mp="1eycmkq77mt1j">
              <thead data-id="element-10347" data-mp="1ewro4phrypye">
                <tr className="border-b border-neutral-100 bg-neutral-50 text-[11px] font-bold uppercase tracking-wider text-neutral-400" data-id="element-10348" data-mp="1jpkufhsw66cl">
                  <th scope="col" className="px-4 py-2.5" data-id="element-10349" data-mp="15z8y16p9xlfn">
                    Week
                  </th>
                  <th scope="col" className="px-4 py-2.5 text-right" data-id="element-10350" data-mp="1ay2dv5s19oiz">
                    Clicked
                  </th>
                  <th scope="col" className="px-4 py-2.5 text-right" data-id="element-10351" data-mp="1ynmwfnk9yjrq">
                    Set up
                  </th>
                  <th scope="col" className="px-4 py-2.5 text-right" data-id="element-10352" data-mp="22m46aev2y8cu">
                    Used it
                  </th>
                  <th scope="col" className="px-4 py-2.5 text-right" data-id="element-10353" data-mp="1tnij26eny55q">
                    Subscribed
                  </th>
                  <th scope="col" className="px-4 py-2.5 text-right" data-id="element-10354" data-mp="d8h6imj5lkyn">
                    Revenue
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100" data-id="element-10355" data-mp="ap2n8umlm7tl">
                {cohorts.map(c => <tr key={c.week} className={c.ripe ? '' : 'bg-neutral-50/50'} data-id="element-10356" data-mp="ar6yxyqqob4f">
                    <td className="px-4 py-2.5 text-[13.5px] font-semibold" data-id="element-10357" data-mp="5pq1yebdl8t4">
                      {c.week}
                      {!c.ripe && <span className="ml-2 rounded-full bg-neutral-100 px-1.5 py-0.5 text-[10.5px] font-bold text-neutral-500" data-id="element-10358" data-mp="jneorrp06q0t">
                          too early
                        </span>}
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono text-[13px]" data-id="element-10359" data-mp="3j55xn853ialm">
                      {fmtNum(c.clicks)}
                    </td>
                    <Cell value={c.setup} base={c.clicks} data-id="element-10360" data-mp="shgo4uxkonn" />
                    <Cell value={c.used} base={c.clicks} data-id="element-10361" data-mp="3s52dck9ibz4j" />
                    {c.ripe ? <Cell value={c.paid} base={c.clicks} highlight data-id="element-10362" data-mp="3s3r96jc5gpcw" /> : <td className="px-4 py-2.5 text-right font-mono text-[13px] text-neutral-300" data-id="element-10363" data-mp="34eyd8mvj2hia">
                        —
                      </td>}
                    <td className="px-4 py-2.5 text-right font-mono text-[13px] font-semibold" data-id="element-10364" data-mp="13ds34newm3xb">
                      {c.ripe ? money(c.revenue) : <span className="text-neutral-300" data-id="element-10365" data-mp="18cbtd00j61vh">—</span>}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </section>

        <div className="mt-9" data-id="element-10366" data-mp="1i2erfemie60o">
          <EventSpecTable data-id="element-10367" data-mp="1i1mwm60qkcbg" />
        </div>
      </div>
    </div>;
}
function Cell({
  value,
  base,
  highlight = false
}: {
  value: number;
  base: number;
  highlight?: boolean;
}) {
  return <td className="px-4 py-2.5 text-right" data-id="element-10368" data-mp="pd7lhukq6e5e">
      <span className={`font-mono text-[13px] ${highlight ? 'font-bold text-[#C2410C]' : ''}`} data-id="element-10369" data-mp="pg3i9fku6ws9">
        {fmtNum(value)}
      </span>
      <span className="ml-1.5 text-[11.5px] text-neutral-400" data-id="element-10370" data-mp="yefjebjbxcyy">
        {Math.round(value / base * 100)}%
      </span>
    </td>;
}